import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './TicketConversation.css';

const TicketConversation = ({ ticketId, ticketEmail, onClose }) => {
  const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';
  const [conversation, setConversation] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState('Update on your support ticket');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  // Clear messages after timeout
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setError(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const getToken = () => {
    try {
      return localStorage.getItem('token');
    } catch (error) {
      console.warn('localStorage not available:', error);
      return null;
    }
  };

  const fetchConversation = async () => {
    // const token = getToken();
    // if (!token) {
    //   setError('Authentication token not found');
    //   setLoading(false);
    //   return;
    // }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/support/ticket/${ticketId}/conversation`,
        // {
        //   headers: {
        //     Authorization: `Bearer ${token}`,
        //   },
        // }
      );

      if (response.data.success) {
        setConversation(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching conversation:', err);
      setError('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ticketId) {
      fetchConversation();
    }
  }, [ticketId]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    // const token = getToken();
    // if (!token) {
    //   setError('Authentication token not found');
    //   return;
    // }

    setSending(true);
    setError(null);

    try {
      // First add the message to conversation (if this endpoint exists)
      let conversationResponse = null;
      try {
        conversationResponse = await axios.post(
          `${API_BASE_URL}/api/support/ticket/${ticketId}/conversation`,
          {
            message: newMessage,
            sendEmail: false, // We'll handle email separately
          },
          // {
          //   headers: {
          //     Authorization: `Bearer ${token}`,
          //     'Content-Type': 'application/json',
          //   },
          // }
        );
      } catch (conversationErr) {
        console.log('Conversation endpoint not available, proceeding with email only');
      }

      // Send email if requested using the dedicated email endpoint
      if (sendEmail && ticketEmail) {
        try {
          const emailResponse = await axios.post(
            `${API_BASE_URL}/api/support/send-ticket-email`,
            {
              email: ticketEmail,
              subject: emailSubject,
              message: newMessage,
              ticketId: ticketId
            },
            // {
            //   headers: {
            //     Authorization: `Bearer ${token}`,
            //     'Content-Type': 'application/json',
            //   },
            // }
          );

          if (emailResponse.status === 200) {
            console.log('Email sent successfully');
            setSuccessMessage('Email notification sent successfully!');
          }
        } catch (emailErr) {
          console.error('Error sending email:', emailErr);
          setError('Message saved but failed to send email notification');
        }
      }

      // Clear form if either conversation was saved or email was sent
      if (conversationResponse?.data?.success || sendEmail) {
        setNewMessage('');
        setSendEmail(false);
        setEmailSubject('Update on your support ticket');

        // Refresh conversation if the endpoint exists
        if (conversationResponse?.data?.success) {
          await fetchConversation();
        } else {
          // If conversation endpoint doesn't exist, add message locally for display
          const newLocalMessage = {
            id: Date.now(), // Temporary ID
            message: newMessage,
            senderType: 'admin',
            senderName: 'Support Team',
            createdAt: new Date().toISOString(),
            ticketId: ticketId
          };
          setConversation(prev => [...prev, newLocalMessage]);
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Test email function
  const handleTestEmail = async () => {
    if (!ticketEmail) {
      setError('No email address available for this ticket');
      return;
    }

    // const token = getToken();
    // if (!token) {
    //   setError('Authentication token not found');
    //   return;
    // }

    setSending(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/support/send-ticket-email`,
        {
          email: ticketEmail,
          subject: "Test Email - Support Ticket",
          message: "This is a test email to verify that the email system is working correctly for your support ticket.",
          ticketId: ticketId
        },
        // {
        //   headers: {
        //     Authorization: `Bearer ${token}`,
        //     'Content-Type': 'application/json',
        //   },
        // }
      );

      if (response.status === 200) {
        setSuccessMessage(`Test email sent successfully to ${ticketEmail}!`);
      }
    } catch (err) {
      console.error('Error sending test email:', err);
      setError('Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="conversation-modal">
        <div className="conversation-content">
          <div className="conversation-header">
            <h3>Loading conversation...</h3>
            <button onClick={onClose} className="close-btn">×</button>
          </div>
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="conversation-modal">
      <div className="conversation-content">
        <div className="conversation-header">
          <h3>Ticket Conversation</h3>
          <div className="header-actions">
            <button
              onClick={handleTestEmail}
              disabled={sending || !ticketEmail}
              className="test-email-btn"
              title={ticketEmail ? `Send test email to ${ticketEmail}` : 'No email available'}
            >
              📧 Test Email
            </button>
            <button onClick={onClose} className="close-btn">×</button>
          </div>
        </div>

        <div className="conversation-messages">
          {conversation.length === 0 ? (
            <div className="no-messages">
              <p>No conversation yet. Start by sending a message below.</p>
            </div>
          ) : (
            conversation.map((message) => (
              <div
                key={message.id}
                className={`message ${message.senderType === 'admin' ? 'admin-message' : 'user-message'}`}
              >
                <div className="message-header">
                  <span className="sender-name">{message.senderName}</span>
                  <span className="message-time">{formatDate(message.createdAt)}</span>
                </div>
                <div className="ticket-message-content">
                  {message.message}
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        <div className="conversation-input">
          <div className="email-info">
            <p><strong>User Email:</strong> {ticketEmail || 'No email available'}</p>
          </div>

          <div className="email-options">
            <label className="email-checkbox">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                disabled={!ticketEmail}
              />
              Send email notification to user {!ticketEmail && '(No email available)'}
            </label>
            {sendEmail && (
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Email subject"
                className="email-subject-input"
              />
            )}
          </div>

          <div className="message-input-container">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message here..."
              className="message-input"
              rows="3"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <button
              onClick={handleSendMessage}
              disabled={sending || !newMessage.trim()}
              className="send-btn"
            >
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketConversation;